// Canner view — the restricted, token-scoped app a canner opens via their private ?ct=<token> link.
// Only Receiving confirmation + On-hand Stock, for the one business they're assigned to. No prices,
// no other businesses, no admin.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { CannerState } from "../lib/api.ts";
import type { Order, OrderLine, RunStatus, StockLot } from "@shared/types.ts";
import { onHandByIngredient, lineArrival, computeRequirements, cansPerBarrel, skuRecipeKey, trackingUrl, componentsForRun } from "@shared/engine.ts";
import { api } from "../lib/api.ts";
import { ReceiveForm } from "../components/ReceiveForm.tsx";
import { StockPanel, type BulkChange } from "../components/StockPanel.tsx";
import { ProductionPanel } from "../components/ProductionPanel.tsx";
import { BatchSheet } from "../components/BatchSheet.tsx";
import { NotificationBell } from "../components/NotificationBell.tsx";
import { Badge, EmptyState, Modal, SkeletonCard } from "../components/ui.tsx";

// Section tabs for the manufacturer portal — icon + label, laid out as a full-width row so every
// tab (incl. Batch Sheet) is always visible and obviously tappable, no horizontal scroll.
const ico = "18";
const MFR_TABS: { id: "materials" | "production" | "batch"; label: string; icon: ReactNode }[] = [
  { id: "materials", label: "Raw Materials", icon: (
    <svg width={ico} height={ico} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>
  ) },
  { id: "production", label: "Production", icon: (
    <svg width={ico} height={ico} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="2" width="8" height="4" rx="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M9 12h6"/><path d="M9 16h6"/></svg>
  ) },
  { id: "batch", label: "Batch Sheet", icon: (
    <svg width={ico} height={ico} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6"/><path d="M10 3v6l-4.5 8A2 2 0 0 0 7.3 20h9.4a2 2 0 0 0 1.8-3L14 9V3"/><path d="M7 14h10"/></svg>
  ) },
];
import { fmtDate, mass, num, todayISO } from "../lib/format.ts";

export default function CannerView({ token }: { token: string }) {
  const [state, setState] = useState<CannerState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"materials" | "production" | "batch">("materials");
  const [focusRun, setFocusRun] = useState<string | null>(null);
  const [open, setOpen] = useState<{ order: Order; line: OrderLine } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setState(await api.cannerState(token));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  const data = state?.data;
  const onHand = useMemo(() => (data ? onHandByIngredient(data.stock) : {}), [data]);

  const stockAct = async (action: "add" | "update" | "delete", lot: Partial<StockLot>) => {
    if (!data) return;
    setBusy(true);
    try {
      await api.stock({ businessId: data.business.id, action, lot }, token);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const bulkStockAct = async (changes: BulkChange[]) => {
    if (!data || changes.length === 0) return;
    const mu = data.business.massUnit || "lb";
    setBusy(true);
    try {
      for (const c of changes) {
        if (c.lotId && c.lbs > 0) await api.stock({ businessId: data.business.id, action: "update", lot: { id: c.lotId, lbs: c.lbs, origQty: c.lbs, uom: mu, lotNumber: c.lotNumber ?? "", expiryDate: c.expiryDate ?? "", receivedDate: c.receivedDate ?? "" } }, token);
        else if (c.lotId) await api.stock({ businessId: data.business.id, action: "delete", lot: { id: c.lotId } }, token);
        else if (c.lbs > 0) await api.stock({ businessId: data.business.id, action: "add", lot: { ingredient: c.ingredient, lbs: c.lbs, origQty: c.lbs, uom: mu, lotNumber: c.lotNumber || undefined, expiryDate: c.expiryDate || undefined, receivedDate: c.receivedDate || undefined } }, token);
      }
      await load();
    } finally {
      setBusy(false);
    }
  };

  const completeAct = async (runId: string, items: { skuId: string; made: number; sent: number; kept: number }[], notes: string) => {
    if (!data) return;
    setBusy(true);
    try {
      await api.complete({ businessId: data.business.id, runId, items, notes }, token);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const statusAct = async (runId: string, status: RunStatus) => {
    if (!data) return;
    setBusy(true);
    try {
      await api.runStatus({ businessId: data.business.id, runId, status }, token);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const updateAct = async (runId: string, patch: { scheduledDate?: string; readyDate?: string; poNumber?: string; batchNumber?: string; notes?: string }) => {
    if (!data) return;
    setBusy(true);
    try {
      await api.runUpdate({ businessId: data.business.id, runId, patch }, token);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const scheduleAct = async (runId: string, action: "propose" | "confirm", date?: string, note?: string) => {
    if (!data) return;
    setBusy(true);
    try {
      await api.runSchedule({ businessId: data.business.id, runId, action, date, note }, token);
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-panel/90 backdrop-blur border-b border-edge">
        <div className="max-w-4xl mx-auto px-4 pt-3 flex items-center gap-3">
          <div>
            <div className="font-semibold text-ink">Manufacturing</div>
            <div className="text-xs text-muted">{state?.business.name || "Canner portal"}</div>
          </div>
          <div className="flex-1" />
          {data && (
            <NotificationBell
              notifications={(data.business.notifications || []).filter((n) => n.audience === "mfr")}
              onMarkRead={async (ids) => { await api.notifRead({ businessId: data.business.id, ids }, token); await load(); }}
              onOpen={(n) => {
                // "missing"/"arrived" send them to Raw Materials (confirm/chase deliveries); else run-centric.
                setTab(n.kind === "missing" || n.kind === "arrived" ? "materials" : "production");
                setFocusRun(n.runId || null);
                if (n.runId) window.setTimeout(() => setFocusRun(null), 4000);
              }}
            />
          )}
        </div>
        {/* Full-width section tabs — always visible, equal columns, icon + label */}
        <div className="max-w-4xl mx-auto px-4 pt-2.5 pb-2.5">
          <nav className="grid grid-cols-3 gap-1.5" role="tablist" aria-label="Sections">
            {MFR_TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t.id)}
                  className={`flex flex-col items-center justify-center gap-1 rounded-xl border px-2 py-2 text-xs font-semibold transition ${
                    active
                      ? "bg-brand text-white border-brand shadow-card"
                      : "bg-paper/70 text-muted border-edge/70 hover:text-ink hover:bg-paper hover:border-edge"
                  }`}
                >
                  {t.icon}
                  <span>{t.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-5">
        {error && <div className="card border-danger/30 bg-danger/5 text-danger text-sm">{error}</div>}
        {loading && !data ? (
          <div className="space-y-4"><SkeletonCard /><SkeletonCard /></div>
        ) : !data ? null : tab === "production" ? (
          <ProductionPanel runs={data.runs} role="mfr" focusRunId={focusRun} skuName={(id) => data.skus.find((s) => s.id === id)?.name || id}
            skuMeta={(id) => { const s = data.skus.find((x) => x.id === id); return { name: s?.name || id, flavour: s?.flavour, strength: s?.strength, imageUrl: s?.imageUrl, productType: s?.productType }; }}
            skuRecipe={(id) => { const s = data.skus.find((x) => x.id === id); if (!s) return null; const lines = data.recipes[skuRecipeKey(s)] || []; if (!lines.length) return null; const cpb = cansPerBarrel(data.business.conversions); return lines.map((l) => ({ ingredient: l.ingredient, lbsPerCan: Number(l.lbsPerCan) || 0, lbsPerBbl: (Number(l.lbsPerCan) || 0) * cpb })); }}
            unit={data.business.unitLabel} busy={busy}
            ingredientProgress={(run) => {
              const rows = computeRequirements(data, run.items).filter((r) => r.requiredLbs > 0.0001);
              if (!rows.length) return null;
              const covered = (r: (typeof rows)[number]) => r.provided || r.onHandLbs >= r.requiredLbs - 1e-6;
              return { have: rows.filter(covered).length, total: rows.length, missing: rows.filter((r) => !covered(r)).map((r) => r.ingredient) };
            }}
            ingredientEta={(run) => {
              const orders = data.orders.filter((o) => o.runId === run.id || (run.orderId && o.id === run.orderId));
              const dates = orders.flatMap((o) => o.lines).filter((l) => l.toOrderLbs > 0).map((l) => l.received?.dateReceived || (l.received?.confirmedAt ? new Date(l.received.confirmedAt).toISOString().slice(0, 10) : "") || l.expectedDelivery).filter(Boolean) as string[];
              return dates.length ? dates.sort().slice(-1)[0] : null;
            }}
            containerWord={(run) => {
              const sku = run.items.map((i) => data.skus.find((s) => s.id === i.skuId)).find(Boolean);
              const t = sku?.productType || "can";
              return t === "bottle" ? "Bottles" : t === "gummy" ? "Gummy packs" : "Cans";
            }}
            components={(run) => componentsForRun(data, run)}
            onSetStatus={statusAct} onComplete={completeAct} onUpdate={updateAct} onSchedule={scheduleAct} />
        ) : tab === "batch" ? (
          <BatchSheet
            skus={data.skus}
            recipes={data.recipes}
            conv={data.business.conversions}
            massUnit={data.business.massUnit}
            unitLabel={data.business.unitLabel}
            businessUnitVolumeL={data.business.unitVolumeL}
          />
        ) : (
          <div className="space-y-6">
            {/* Section 1 — incoming deliveries we've sent, to be confirmed on arrival */}
            <section className="rounded-2xl border border-brand/25 bg-brand/[0.03] p-4">
              <span className="inline-block text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-brand/10 text-brand mb-1">① Incoming · confirm deliveries</span>
              <h2 className="text-sm font-semibold">Deliveries to confirm</h2>
              <p className="text-muted text-xs mt-0.5">Stock we've sent you — confirm each shipment <span className="font-medium text-ink">as it lands</span> (lot #, expiry, quantity, photo). Confirmed amounts drop into your on-site stock below.</p>
              <div className="mt-3"><ReceivingList data={data} onOpen={(order, line) => setOpen({ order, line })} /></div>
            </section>

            {/* Divider between the two purposes */}
            <div className="flex items-center gap-3 pt-1">
              <div className="flex-1 border-t border-edge" />
              <span className="text-[11px] font-semibold text-muted uppercase tracking-wide">Everything on site</span>
              <div className="flex-1 border-t border-edge" />
            </div>

            {/* Section 2 — the full on-site stocktake, always kept current */}
            <section className="rounded-2xl border border-ok/25 bg-ok/[0.03] p-4">
              <StockPanel
                ingredients={data.ingredients.map((i) => i.name)}
                lots={data.stock}
                onHand={onHand}
                busy={busy}
                massUnit={data.business.massUnit || "lb"}
                pill="② On-site stocktake · keep current"
                title="All stock on site"
                subtitle="Your full ingredient stocktake — everything you physically hold. Keep quantities up to date so our ordering stays accurate."
                onAdd={(lot) => stockAct("add", lot)}
                onUpdate={(lot) => stockAct("update", lot)}
                onDelete={(idv) => stockAct("delete", { id: idv })}
                onBulk={bulkStockAct}
              />
            </section>
          </div>
        )}
      </main>

      {open && data && (
        <Modal title={`Receive · ${open.line.ingredient}`} onClose={() => setOpen(null)} wide>
          <ReceiveForm businessId={data.business.id} orderId={open.order.id} line={open.line} token={token} massUnit={data.business.massUnit || "lb"} onDone={() => { setOpen(null); void load(); }} />
        </Modal>
      )}
    </div>
  );
}

function ReceivingList({ data, onOpen }: { data: CannerState["data"]; onOpen: (o: Order, l: OrderLine) => void }) {
  const massUnit = data.business.massUnit || "lb";
  const orders = data.orders.filter((o) => o.lines.some((l) => l.toOrderLbs > 0));
  if (orders.length === 0) return <div className="card"><EmptyState title="Nothing to receive" sub="When the client places an order it will appear here to confirm." /></div>;
  const today = todayISO();
  const arrivedToConfirm = orders.flatMap((o) => o.lines.filter((l) => l.toOrderLbs > 0)).filter((l) => lineArrival(l, today) === "arrived").length;
  return (
    <div className="space-y-4">
      {arrivedToConfirm > 0 && (
        <div className="card bg-danger/5 border-danger/30">
          <div className="text-sm font-semibold text-danger">⚠ {arrivedToConfirm} item{arrivedToConfirm !== 1 ? "s" : ""} arrived — please check &amp; confirm now</div>
          <p className="text-danger/80 text-xs mt-0.5">Record the lot number, expiry, quantity received (good/damaged) and add photo evidence for each. This is your record — confirm exactly what landed.</p>
        </div>
      )}
      <p className="text-muted text-sm">Confirm each ingredient as it arrives — record the lot number, expiry, quantities, and add photo evidence.</p>
      {orders.map((o) => {
        const lines = o.lines.filter((l) => l.toOrderLbs > 0);
        const received = lines.filter((l) => l.received?.confirmedAt).length;
        const run = data.runs.find((r) => r.id === o.runId || r.orderId === o.id);
        return (
          <div key={o.id} className="card">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold">Ingredients for production {run ? fmtDate(run.scheduledDate) : "—"}</div>
                <div className="text-[11px] text-muted">Order placed {fmtDate(o.createdAt)}{run ? ` · ${num(run.items.reduce((a, i) => a + i.cans, 0))} ${data.business.unitLabel}s` : ""}</div>
              </div>
              <Badge tone={received === lines.length ? "ok" : received > 0 ? "brand" : "warn"}>{received}/{lines.length} received</Badge>
            </div>

            {/* Desktop: table. Mobile: stacked cards — no horizontal scroll. */}
            <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead><tr><th className="th">Ingredient</th><th className="th">Supplier</th><th className="th text-right">Expected</th><th className="th">Status</th><th className="th text-right"></th></tr></thead>
              <tbody>
                {lines.map((l) => {
                  const state = lineArrival(l, today);
                  const turl = trackingUrl(l.carrier, l.trackingNumber);
                  const track = [l.carrier, l.trackingNumber].filter(Boolean).join(" ");
                  const hasDates = !!(l.shippedDate || l.expectedDelivery);
                  return (
                  <tr key={l.id} className={state === "arrived" ? "bg-danger/5" : ""}>
                    <td className="td font-medium">{l.ingredient}
                      {(hasDates || track) && (
                        <div className="text-[11px] text-muted font-normal">
                          {l.shippedDate ? `Shipped ${fmtDate(l.shippedDate)}` : ""}{l.shippedDate && l.expectedDelivery ? " · " : ""}{l.expectedDelivery ? `ETA ${fmtDate(l.expectedDelivery)}` : ""}
                          {track && hasDates ? " · " : ""}
                          {track && (turl
                            ? <a href={turl} target="_blank" rel="noreferrer" className="text-brand hover:underline">{track}</a>
                            : track)}
                        </div>
                      )}
                    </td>
                    <td className="td text-muted">{l.supplier || "—"}</td>
                    <td className="td text-right tabular-nums">{l.qtyOrdered ? `${num(l.qtyOrdered)} ${l.uom || massUnit}` : mass(l.toOrderLbs, massUnit)}</td>
                    <td className="td">{
                      state === "received" ? <Badge tone="ok">✓ {fmtDate(l.received!.dateReceived)}</Badge>
                        : state === "arrived" ? <Badge tone="danger">Arrived — confirm</Badge>
                        : state === "in_transit" ? <Badge tone="brand">In transit</Badge>
                        : <Badge tone="muted">Awaiting shipment</Badge>
                    }</td>
                    <td className="td text-right"><button className={`btn-sm whitespace-nowrap ${state === "arrived" ? "bg-brand text-white" : "border border-edge hover:bg-paper"}`} onClick={() => onOpen(o, l)}>{l.received?.confirmedAt ? "View / edit" : "Confirm"}</button></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            <div className="sm:hidden divide-y divide-edge/50 -mb-1">
              {lines.map((l) => {
                const state = lineArrival(l, today);
                const turl = trackingUrl(l.carrier, l.trackingNumber);
                const track = [l.carrier, l.trackingNumber].filter(Boolean).join(" ");
                const hasDates = !!(l.shippedDate || l.expectedDelivery);
                const qty = l.qtyOrdered ? `${num(l.qtyOrdered)} ${l.uom || massUnit}` : mass(l.toOrderLbs, massUnit);
                return (
                  <div key={l.id} className={`py-3 ${state === "arrived" ? "-mx-4 px-4 bg-danger/5" : ""}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-ink">{l.ingredient}</div>
                        <div className="text-[11px] text-muted">{l.supplier || "No supplier"} · <span className="tabular-nums">{qty}</span></div>
                        {(hasDates || track) && (
                          <div className="text-[11px] text-muted mt-0.5">
                            {l.shippedDate ? `Shipped ${fmtDate(l.shippedDate)}` : ""}{l.shippedDate && l.expectedDelivery ? " · " : ""}{l.expectedDelivery ? `ETA ${fmtDate(l.expectedDelivery)}` : ""}
                            {track && hasDates ? " · " : ""}
                            {track && (turl ? <a href={turl} target="_blank" rel="noreferrer" className="text-brand hover:underline">{track}</a> : track)}
                          </div>
                        )}
                      </div>
                      <div className="shrink-0">{
                        state === "received" ? <Badge tone="ok">✓ {fmtDate(l.received!.dateReceived)}</Badge>
                          : state === "arrived" ? <Badge tone="danger">Arrived</Badge>
                          : state === "in_transit" ? <Badge tone="brand">In transit</Badge>
                          : <Badge tone="muted">Awaiting</Badge>
                      }</div>
                    </div>
                    <button className={`btn-sm w-full mt-2 whitespace-nowrap ${state === "arrived" ? "bg-brand text-white" : "border border-edge hover:bg-paper"}`} onClick={() => onOpen(o, l)}>{l.received?.confirmedAt ? "View / edit" : "Confirm arrival"}</button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
